import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SignOutButton from '@/components/SignOutButton';
import { Button } from '@/components/ui/button';
import {
  Heart, ArrowLeft, CreditCard, Trash2, ShieldCheck, Download, AlertTriangle,
} from 'lucide-react';

const NAVY = '#1D2D44';
const GOLD = '#C9A84C';
const INK = '#0F1A2A';
const PAPER = '#16243D';
const MUTED = '#8FA0BD';
const LINE = 'rgba(201,168,76,0.22)';
const TEXT = '#F5F1E6';

const Section = ({ title, kicker, children, testid }) => (
  <section
    data-testid={testid}
    className="rounded-2xl p-6 sm:p-8 mb-6"
    style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
  >
    {kicker && (
      <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>
        {kicker}
      </p>
    )}
    <h2 className="font-serif text-2xl sm:text-3xl mb-5" style={{ color: TEXT }}>
      {title}
    </h2>
    {children}
  </section>
);

const Row = ({ label, value, valueTestid }) => (
  <div className="flex items-center justify-between py-2.5" style={{ borderBottom: `1px solid ${LINE}` }}>
    <span className="text-sm" style={{ color: MUTED }}>{label}</span>
    <span className="text-sm font-medium" style={{ color: TEXT }} data-testid={valueTestid}>{value}</span>
  </div>
);

const GhostButton = ({ children, testid }) => (
  <Button
    variant="outline"
    data-testid={testid}
    className="rounded-full px-5 py-5"
    style={{ borderColor: GOLD, color: TEXT, backgroundColor: 'transparent' }}
  >
    {children}
  </Button>
);

const PLAN_LABELS = { free: 'Free', founding_member: 'Founding Member' };

const AccountSettingsPage = () => {
  const { user, axiosAuth, logout } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');

  // Load lightweight per-user settings on mount (persists across sessions).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axiosAuth().get('/settings');
        if (!active) return;
        setAutoRenew(Boolean(data.auto_renew));
        setSubscriptionStatus(data.subscription_status || 'free');
      } catch (e) {
        // Fall back to defaults; no billing logic involved.
      }
    })();
    return () => { active = false; };
  }, [axiosAuth]);

  const handleToggleAutoRenew = async () => {
    const next = !autoRenew;
    setAutoRenew(next); // optimistic
    try {
      await axiosAuth().put('/settings', { auto_renew: next });
    } catch (e) {
      setAutoRenew(!next); // revert on failure
    }
  };

  const closeDeleteFlow = () => {
    setShowPasswordModal(false);
    setShowDeleteModal(false);
    setDeletePassword('');
    setPasswordError('');
    setDeleteError('');
  };

  const handleVerifyPassword = async () => {
    if (!deletePassword) {
      setPasswordError('Please enter your password.');
      return;
    }
    setVerifying(true);
    setPasswordError('');
    try {
      await axiosAuth().post('/account/verify-password', { password: deletePassword });
      setShowPasswordModal(false);
      setShowDeleteModal(true); // show the final confirmation
    } catch (e) {
      const status = e?.response?.status;
      setPasswordError(
        status === 403
          ? 'This account cannot be deleted.'
          : 'Incorrect password. Please try again.'
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await axiosAuth().delete('/account', { data: { password: deletePassword } });
      // Clear stored JWT + authenticated app state, then leave the app entirely.
      logout();
      window.location.replace('https://ourloveconnection.co');
    } catch (e) {
      const status = e?.response?.status;
      setDeleteError(
        status === 403
          ? 'This account cannot be deleted.'
          : status === 401
          ? 'Password verification failed. Please try again.'
          : 'Account deletion failed. Please try again.'
      );
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: INK }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur"
        style={{ backgroundColor: 'rgba(15,26,42,0.85)', borderBottom: `1px solid ${LINE}` }}
      >
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/profile"
            data-testid="settings-back-link"
            className="flex items-center text-sm"
            style={{ color: MUTED }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Back to Profile</span>
          </Link>
          <Link to="/dashboard" className="font-serif text-lg" style={{ color: TEXT }} data-testid="brand-logo-link">
            Our Love <Heart className="inline-block w-4 h-4 mx-1" style={{ color: GOLD }} strokeWidth={1.5} fill={GOLD} /> Connection
          </Link>
          <div className="flex items-center justify-end w-20 sm:w-auto">
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="account-settings-page">
        {/* Title */}
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>
            Account
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl mb-3" style={{ color: TEXT }}>
            Account Settings
          </h1>
          <p className="max-w-xl mx-auto leading-relaxed text-sm sm:text-base" style={{ color: MUTED }}>
            {user?.name ? `Signed in as ${user.name}. ` : ''}Manage your subscription, account, and privacy preferences.
          </p>
        </div>

        {/* 1. Subscription Overview */}
        <Section title="Subscription Overview" kicker="Section 1 · plan" testid="settings-subscription-overview">
          <div className="mb-6">
            <Row label="Current Plan" value={PLAN_LABELS[subscriptionStatus] || 'Free'} valueTestid="subscription-current-plan" />
            <Row label="Status" value="UI only" valueTestid="subscription-status" />
            <Row label="Renewal" value="Not available yet" valueTestid="subscription-renewal" />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="upgrade-plan-btn"
              className="rounded-full px-6 py-5"
              style={{ backgroundColor: GOLD, color: NAVY, fontWeight: 600 }}
            >
              <CreditCard className="w-4 h-4 mr-2" /> Upgrade Plan
            </Button>
            <GhostButton testid="change-plan-btn">Change Plan</GhostButton>
            <GhostButton testid="cancel-subscription-btn">Cancel Subscription</GhostButton>
          </div>
        </Section>

        {/* 2. Account Management */}
        <Section title="Account Management" kicker="Section 2 · account" testid="settings-account-management">
          <p className="text-sm mb-5" style={{ color: MUTED }}>
            Permanently remove your account and all associated data.
          </p>
          <Button
            data-testid="delete-account-btn"
            onClick={() => { setShowPasswordModal(true); setDeletePassword(''); setPasswordError(''); }}
            variant="outline"
            className="rounded-full px-6 py-5"
            style={{ borderColor: '#C96A6A', color: '#F0C7C7', backgroundColor: 'transparent' }}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete Account
          </Button>
          <p className="text-xs mt-3 flex items-center" style={{ color: MUTED }}>
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" style={{ color: '#C96A6A' }} />
            This action cannot be undone.
          </p>
        </Section>

        {/* 3. Privacy Controls */}
        <Section title="Privacy Controls" kicker="Section 3 · privacy" testid="settings-privacy-controls">
          <div className="flex flex-wrap gap-3">
            <GhostButton testid="download-data-btn">
              <Download className="w-4 h-4 mr-2" /> Download My Data
            </GhostButton>
            <GhostButton testid="manage-privacy-btn">
              <ShieldCheck className="w-4 h-4 mr-2" /> Manage Privacy Settings
            </GhostButton>
          </div>
        </Section>

        {/* 4. Billing */}
        <Section title="Billing" kicker="Section 4 · billing" testid="settings-billing">
          <div
            className="flex items-center justify-between rounded-xl p-4 mb-3"
            style={{ backgroundColor: INK, border: `1px solid ${LINE}` }}
          >
            <div>
              <p className="text-sm font-medium" style={{ color: TEXT }}>Auto-renew</p>
              <p className="text-xs mt-0.5" style={{ color: MUTED }}>Automatically renew your plan at the end of each term.</p>
            </div>
            <button
              type="button"
              data-testid="auto-renew-toggle"
              role="switch"
              aria-checked={autoRenew}
              onClick={handleToggleAutoRenew}
              className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
              style={{ backgroundColor: autoRenew ? GOLD : 'rgba(255,255,255,0.15)' }}
            >
              <span
                className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                style={{ transform: autoRenew ? 'translateX(22px)' : 'translateX(4px)' }}
              />
            </button>
          </div>
          <p className="text-xs" style={{ color: MUTED }} data-testid="billing-status-text">
            Not connected to billing system
          </p>
        </Section>
      </main>

      {/* Step 1: Password re-authentication modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4" data-testid="delete-password-modal">
          <div className="w-full max-w-md rounded-2xl p-8" style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}>
            <h3 className="font-serif text-2xl mb-2" style={{ color: TEXT }}>Confirm it's you</h3>
            <p className="text-sm mb-5" style={{ color: MUTED }}>
              For your security, please re-enter your password to continue deleting your account.
            </p>
            <input
              type="password"
              data-testid="delete-password-input"
              value={deletePassword}
              onChange={(e) => { setDeletePassword(e.target.value); setPasswordError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyPassword(); }}
              placeholder="Current password"
              autoFocus
              className="w-full rounded-xl px-4 py-3 mb-3 text-sm outline-none"
              style={{ backgroundColor: INK, border: `1px solid ${LINE}`, color: TEXT }}
            />
            {passwordError && (
              <p className="text-sm mb-4 rounded-lg px-4 py-3" data-testid="delete-password-error"
                 style={{ color: '#F0C7C7', backgroundColor: 'rgba(201,106,106,0.12)', border: '1px solid rgba(201,106,106,0.4)' }}>
                {passwordError}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <Button
                data-testid="delete-password-cancel-btn"
                onClick={closeDeleteFlow}
                disabled={verifying}
                variant="outline"
                className="flex-1 rounded-full"
                style={{ borderColor: GOLD, color: TEXT, backgroundColor: 'transparent' }}
              >
                Cancel
              </Button>
              <Button
                data-testid="delete-password-continue-btn"
                onClick={handleVerifyPassword}
                disabled={verifying}
                className="flex-1 rounded-full"
                style={{ backgroundColor: GOLD, color: NAVY, fontWeight: 600 }}
              >
                {verifying ? 'Verifying…' : 'Continue'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Delete Account confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4" data-testid="delete-account-modal">
          <div
            className="w-full max-w-md rounded-2xl p-8"
            style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5" style={{ color: '#C96A6A' }} />
              <h3 className="font-serif text-2xl" style={{ color: TEXT }}>Are you sure?</h3>
            </div>
            <p className="text-sm mb-6" style={{ color: MUTED }}>
              This will permanently delete your account, your Relationship Map, your profile, and all associated data. This cannot be undone.
            </p>
            {deleteError && (
              <p className="text-sm mb-6 rounded-lg px-4 py-3" data-testid="delete-error-notice"
                 style={{ color: '#F0C7C7', backgroundColor: 'rgba(201,106,106,0.12)', border: '1px solid rgba(201,106,106,0.4)' }}>
                {deleteError}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="delete-cancel-btn"
                onClick={closeDeleteFlow}
                disabled={deleting}
                variant="outline"
                className="flex-1 rounded-full"
                style={{ borderColor: GOLD, color: TEXT, backgroundColor: 'transparent' }}
              >
                Cancel
              </Button>
              <Button
                data-testid="delete-confirm-btn"
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 rounded-full"
                style={{ backgroundColor: '#C96A6A', color: '#1A0E0E', fontWeight: 600 }}
              >
                {deleting ? 'Deleting…' : 'Yes, Delete My Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettingsPage;
